import { useState, type JSX } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Divider,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useSetContainerRestriction } from '@/hooks/useVisibility';
import { useWhoami } from '@/hooks/useWhoami';
import type { SetRestrictionInput } from '@/services/api/endpoints';
import { PrincipalPicker } from './PrincipalPicker';
import { SelectedPrincipals } from './SelectedPrincipals';
import { buildPermissions, groupLabel, useVisibilityForm } from './useVisibilityForm';

const useStyles = makeStyles({
  content: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  step: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0 },
  stepLabel: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  option: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  optionHint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  loading: { display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalL },
  errorActions: { display: 'flex', justifyContent: 'flex-end' },
});

/**
 * Ce dont la popup a réellement besoin : un identifiant et un nom à afficher.
 *
 * DÉLIBÉRÉMENT PLUS ÉTROIT QUE `Container`. L'onglet d'administration liste les
 * espaces via `outlook-admin-list-spaces`, qui renvoie des agrégats et non des
 * `Container` complets (pas de `m365GroupId`, pas de dates) : exiger un
 * `Container` obligerait à en fabriquer un faux, avec des champs inventés qu'un
 * futur lecteur croirait vrais. `Container` reste assignable à ce type, donc les
 * appels existants (arbre, recherche) ne changent pas.
 */
export interface VisibilityDialogTarget {
  id: string;
  name: string;
}

export interface ContainerVisibilityDialogProps {
  container: VisibilityDialogTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Popup « Gérer la visibilité » d'un projet ou d'un espace de service.
 *
 * Deux blocs : la visibilité (publique ou restreinte), puis — si restreinte — les
 * personnes et groupes Entra autorisés. Volontairement ouverte à TOUS les
 * utilisateurs : la règle produit est que la visibilité se gère entre pairs, pas
 * par un administrateur.
 *
 * Une règle serveur doit rester lisible à l'écran : « restreint sans
 * destinataire » est ramené à « public ». Les administrateurs de l'add-in, eux,
 * gardent accès à tout espace (migration 0020) ; ce n'est plus rappelé dans
 * chaque libellé, mais cela reste vrai — c'est ce qui rend un espace verrouillé
 * par erreur récupérable.
 */
export function ContainerVisibilityDialog({
  container,
  open,
  onOpenChange,
}: ContainerVisibilityDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={(_e, d) => onOpenChange(d.open)}>
      <DialogSurface>
        {/* Corps monté UNIQUEMENT pendant l'ouverture, et c'est tout le point :
            son état (brouillon de droits, message post-enregistrement, erreur de
            la mutation) vit exactement le temps d'une session de la popup et
            disparaît à la fermeture.

            Avant, ce composant restait monté en permanence et remettait cet état à
            zéro dans un `useEffect` sur `open`. Deux défauts : la remise à zéro
            était un effet de bord réactif là où il s'agissait d'une durée de vie
            (react-hooks/set-state-in-effect), et surtout elle n'était garantie que
            si toutes les fermetures passaient par le même chemin — un parent
            repassant `open` à faux autrement laissait le message du tour précédent
            réapparaître à la réouverture. Ici la question ne se pose plus. */}
        {open && (
          <VisibilityDialogBody container={container} onClose={() => onOpenChange(false)} />
        )}
      </DialogSurface>
    </Dialog>
  );
}

function VisibilityDialogBody({
  container,
  onClose,
}: {
  container: VisibilityDialogTarget;
  onClose: () => void;
}): JSX.Element {
  const styles = useStyles();
  const {
    query,
    form,
    initial,
    isDirty,
    selectedUserKeys,
    selectedGroupIds,
    setRestricted,
    toggleUser,
    toggleGroup,
    removeUser,
    removeGroup,
  } = useVisibilityForm(container.id);
  const whoami = useWhoami();
  const save = useSetContainerRestriction();
  // Message post-enregistrement (synchro de groupe incomplète, ou verdict du
  // serveur différent de la demande) : on garde la popup ouverte pour le lire.
  const [notice, setNotice] = useState<string | null>(null);

  const initialCount = initial ? initial.users.length + initial.groups.length : 0;
  const recipientCount = form ? form.users.length + form.groups.length : 0;

  // On identifie l'utilisateur courant par son UPN, comparé en minuscules aux
  // e-mails retenus. UPN inconnu ⇒ pas d'avertissement : mieux vaut se taire
  // que déclencher une fausse alerte.
  const selfUpn = whoami.data?.upn?.toLowerCase() ?? null;
  const selfSelected = Boolean(
    selfUpn && form?.users.some((u) => u.email?.toLowerCase() === selfUpn),
  );
  const warnSelfLockout =
    Boolean(form?.restricted) && recipientCount > 0 && selfUpn !== null && !selfSelected;
  const willClearRights = Boolean(form && !form.restricted && isDirty && initialCount > 0);

  const submit = () => {
    if (!form) return;
    const input: SetRestrictionInput = {
      containerId: container.id,
      isRestricted: form.restricted,
      permissions: buildPermissions(form),
    };
    setNotice(null);
    save.mutate(input, {
      onSuccess: (res) => {
        const messages: string[] = [];
        // Le serveur a le dernier mot sur `isRestricted` : on ne referme jamais
        // la popup en laissant croire à un résultat qu'il n'a pas appliqué.
        if (res.isRestricted !== input.isRestricted) {
          messages.push(
            res.isRestricted
              ? "Enregistré, mais le serveur a finalement marqué cet espace comme restreint."
              : "Enregistré : faute de destinataire, cet espace reste visible par tout le monde.",
          );
        }
        const failed = res.groupSync.filter((g) => !g.ok);
        if (failed.length > 0) {
          const names = failed
            .map((f) => {
              const known = form.groups.find((g) => g.groupId === f.groupId);
              return known ? groupLabel(known) : 'groupe sans nom';
            })
            .join(', ');
          messages.push(
            failed.length > 1
              ? `Les droits sont enregistrés, mais les membres des groupes ${names} n'ont pas pu être synchronisés maintenant : ils seront pris en compte au prochain passage de la synchronisation, dans quelques minutes.`
              : `Les droits sont enregistrés, mais les membres du groupe ${names} n'ont pas pu être synchronisés maintenant : ils seront pris en compte au prochain passage de la synchronisation, dans quelques minutes.`,
          );
        }
        if (messages.length > 0) setNotice(messages.join(' '));
        else onClose();
      },
    });
  };

  const errMsg = save.error instanceof Error ? save.error.message : null;

  return (
    <DialogBody>
          {/* Le nom de l'espace porte déjà son numéro de projet : un sous-titre
              « Projet 26016 » ne répétait qu'une information déjà lisible. */}
          <DialogTitle>Visibilité : {container.name}</DialogTitle>
          <DialogContent className={styles.content}>
            {query.isLoading || (!form && !query.error) ? (
              <div className={styles.loading}>
                <Spinner size="tiny" label="Lecture des droits actuels…" />
              </div>
            ) : null}

            {/* Sans état lu, il n'y a rien à éditer : erreur bloquante. Avec un
                état lu précédemment, on édite mais on dit qu'il peut avoir vieilli. */}
            {query.error &&
              (form ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    Impossible de revérifier les droits à l'instant ({query.error.message}) :
                    l'état affiché est celui de la dernière lecture réussie.
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <>
                  <MessageBar intent="error">
                    <MessageBarBody>
                      Impossible de lire les droits actuels : {query.error.message}
                    </MessageBarBody>
                  </MessageBar>
                  <div className={styles.errorActions}>
                    <Button appearance="secondary" onClick={() => void query.refetch()}>
                      Réessayer
                    </Button>
                  </div>
                </>
              ))}

            {form && (
              <>
                <div className={styles.step}>
                  <Text className={styles.stepLabel}>Visibilité</Text>
                  <RadioGroup
                    value={form.restricted ? 'restricted' : 'public'}
                    onChange={(_e, d) => setRestricted(d.value === 'restricted')}
                  >
                    <Radio
                      value="public"
                      label={
                        <span className={styles.option}>
                          <span>Visibilité publique</span>
                          <span className={styles.optionHint}>
                            Tout le monde voit cet espace et son contenu.
                          </span>
                        </span>
                      }
                    />
                    <Radio
                      value="restricted"
                      label={
                        <span className={styles.option}>
                          <span>Restreindre la visibilité</span>
                          <span className={styles.optionHint}>
                            Seules les personnes et les groupes désignés voient cet espace et son
                            contenu.
                          </span>
                        </span>
                      }
                    />
                  </RadioGroup>
                </div>

                {willClearRights && (
                  <MessageBar intent="info">
                    <MessageBarBody>
                      En enregistrant, les {initialCount} droit(s) actuellement enregistré(s) sur cet
                      espace seront supprimés.
                    </MessageBarBody>
                  </MessageBar>
                )}

                {form.restricted && (
                  <>
                    <Divider />
                    <div className={styles.step}>
                      <Text className={styles.stepLabel}>Accès autorisé</Text>
                      <PrincipalPicker
                        // Toujours vrai ici : ce corps n'est monté que pendant
                        // l'ouverture de la popup.
                        enabled
                        selectedUserKeys={selectedUserKeys}
                        selectedGroupIds={selectedGroupIds}
                        onToggleUser={toggleUser}
                        onToggleGroup={toggleGroup}
                      />
                    </div>

                    <SelectedPrincipals
                      users={form.users}
                      groups={form.groups}
                      selfUpn={selfUpn}
                      onRemoveUser={removeUser}
                      onRemoveGroup={removeGroup}
                    />

                    {/* Règle serveur : une restriction sans destinataire est ramenée
                        à « public ». Autant l'annoncer avant l'enregistrement. */}
                    {recipientCount === 0 && (
                      <MessageBar intent="info">
                        <MessageBarBody>
                          Sans aucun destinataire, la restriction n'a pas d'effet : l'espace restera
                          visible par tout le monde.
                        </MessageBarBody>
                      </MessageBar>
                    )}

                    {/* On informe, on ne bloque pas : c'est la règle voulue. */}
                    {warnSelfLockout && (
                      <MessageBar intent="warning">
                        <MessageBarBody>
                          Vous ne figurez pas dans la liste : en enregistrant, vous perdrez l'accès à
                          cet espace, sauf si vous êtes membre d'un des groupes retenus.
                        </MessageBarBody>
                      </MessageBar>
                    )}
                  </>
                )}
              </>
            )}

            {notice && (
              <MessageBar intent="warning">
                <MessageBarBody>{notice}</MessageBarBody>
              </MessageBar>
            )}

            {errMsg && (
              <MessageBar intent="error">
                <MessageBarBody>{errMsg}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            {notice ? (
              // L'enregistrement a réussi, mais le résultat mérite une lecture
              // (verdict serveur différent de la demande, ou synchro de groupe
              // reportée). Le formulaire est TOUJOURS à l'écran : n'offrir que
              // « Fermer » enfermerait l'utilisateur dans une impasse alors que le
              // message l'invite précisément à corriger quelque chose — typiquement
              // « faute de destinataire, cet espace reste visible par tout le
              // monde », qui appelle à désigner un destinataire. « Continuer à
              // modifier » efface donc simplement le message et rend la main au
              // formulaire (l'état lu a été rafraîchi entre-temps, donc
              // « Enregistrer » ne se réactive qu'après un nouveau changement).
              <>
                <Button appearance="secondary" onClick={() => setNotice(null)}>
                  Continuer à modifier
                </Button>
                <Button appearance="primary" onClick={onClose}>
                  Fermer
                </Button>
              </>
            ) : (
              <>
                <Button
                  appearance="secondary"
                  onClick={onClose}
                  disabled={save.isPending}
                >
                  Annuler
                </Button>
                <Button
                  appearance="primary"
                  onClick={submit}
                  disabled={!form || save.isPending || !isDirty}
                >
                  {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
              </>
            )}
          </DialogActions>
    </DialogBody>
  );
}
